<template>
  <div class="trip-detail">
    <!-- Loading State -->
    <div v-if="isLoading" class="loading-container">
      <div class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-3 text-muted">Loading trip details...</p>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-container">
      <div class="container py-5">
        <div class="alert alert-danger text-center" role="alert">
          <i class="fas fa-exclamation-triangle fa-3x mb-3"></i>
          <h4>{{ error }}</h4>
          <p>The trip you're looking for might not be available.</p>
          <router-link to="/trips" class="btn btn-primary">
            <i class="fas fa-arrow-left me-2"></i>
            Back to Trips
          </router-link>
        </div>
      </div>
    </div>

    <!-- Trip Content -->
    <div v-else-if="trip">
      <!-- Hero Section with Images -->
      <section class="trip-hero">
        <div class="hero-gallery">
          <div
            v-if="trip.images && trip.images.length > 0"
            class="main-image-container"
          >
            <img
              :src="currentImage"
              :alt="trip.place"
              class="main-image"
              @click="openGallery(currentImageIndex)"
            />

            <!-- Image Navigation -->
            <div v-if="trip.images.length > 1" class="image-nav">
              <button
                v-for="(image, index) in trip.images"
                :key="index"
                :class="{ active: index === currentImageIndex }"
                class="nav-dot"
                @click="currentImageIndex = index"
              ></button>
            </div>

            <!-- Image Counter -->
            <div v-if="trip.images.length > 1" class="image-counter">
              {{ currentImageIndex + 1 }} / {{ trip.images.length }}
            </div>
          </div>

          <div v-else class="main-image-placeholder">
            <i class="fas fa-image fa-5x text-muted"></i>
          </div>
        </div>

        <!-- Hero Content Overlay -->
        <div class="hero-overlay">
          <div class="container">
            <div class="hero-content">
              <h1 class="trip-title">{{ trip.place }}</h1>
              <div class="trip-badges">
                <span class="badge duration-badge">
                  <i class="fas fa-clock me-1"></i>
                  {{ trip.days }} Days
                </span>
                <span class="badge price-badge">
                  <i class="fas fa-rupee-sign me-1"></i>
                  &#8377;{{ trip.price }} per person
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Trip Details Section -->
      <section class="trip-details">
        <div class="container">
          <div class="row">
            <!-- Main Content -->
            <div class="col-lg-8">
              <!-- Trip Info -->
              <div class="card mb-4">
                <div class="card-body">
                  <h3 class="card-title">
                    <i class="fas fa-info-circle me-2 text-primary"></i>
                    Trip Overview
                  </h3>
                  <p class="trip-description">{{ trip.description }}</p>

                  <!-- Trip Meta Info -->
                  <div class="trip-meta-grid">
                    <div class="meta-item">
                      <div class="meta-icon">
                        <i class="fas fa-calendar-alt text-primary"></i>
                      </div>
                      <div class="meta-content">
                        <strong>Departure Date</strong>
                        <span>{{ formatDate(trip.date) }}</span>
                      </div>
                    </div>

                    <div class="meta-item">
                      <div class="meta-icon">
                        <i class="fas fa-clock text-primary"></i>
                      </div>
                      <div class="meta-content">
                        <strong>Duration</strong>
                        <span>{{ trip.days }} Days</span>
                      </div>
                    </div>

                    <div class="meta-item">
                      <div class="meta-icon">
                        <i class="fas fa-rupee-sign text-primary"></i>
                      </div>
                      <div class="meta-content">
                        <strong>Price</strong>
                        <span>&#8377;{{ trip.price }} per person</span>
                      </div>
                    </div>

                    <div class="meta-item">
                      <div class="meta-icon">
                        <i class="fas fa-map-marker-alt text-primary"></i>
                      </div>
                      <div class="meta-content">
                        <strong>Destination</strong>
                        <span>{{ trip.place }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Video Section -->
              <div v-if="trip.video" class="card mb-4">
                <div class="card-body">
                  <h3 class="card-title">
                    <i class="fas fa-play-circle me-2 text-primary"></i>
                    Trip Preview
                  </h3>
                  <div class="video-container">
                    <video :src="trip.video" controls class="trip-video">
                      Your browser does not support the video tag.
                    </video>
                  </div>
                </div>
              </div>

              <!-- Itinerary Section -->
              <div v-if="trip.itineraryPdf" class="card mb-4">
                <div class="card-body">
                  <h3 class="card-title">
                    <i class="fas fa-route me-2 text-primary"></i>
                    Detailed Itinerary
                  </h3>
                  <p class="mb-3">
                    Download our detailed itinerary to see what amazing
                    experiences await you on this journey.
                  </p>
                  <a
                    :href="trip.itineraryPdf"
                    target="_blank"
                    class="btn btn-outline-primary"
                  >
                    <i class="fas fa-download me-2"></i>
                    Download Itinerary (PDF)
                  </a>
                </div>
              </div>
            </div>

            <!-- Sidebar -->
            <div class="col-lg-4">
              <!-- Booking Card -->
              <div class="card booking-card mb-4">
                <div class="card-body text-center">
                  <h4 class="booking-title">Ready to Book?</h4>
                  <div class="price-display">
                    <span class="price">&#8377;{{ trip.price }}</span>
                    <span class="price-unit">per person</span>
                  </div>

                  <div class="booking-info mb-4">
                    <div class="info-item">
                      <i class="fas fa-calendar me-2"></i>
                      {{ formatDate(trip.date) }}
                    </div>
                    <div class="info-item">
                      <i class="fas fa-clock me-2"></i>
                      {{ trip.days }} Days Adventure
                    </div>
                  </div>

                  <a
                    v-if="trip.googleSheetUrl"
                    :href="trip.googleSheetUrl"
                    target="_blank"
                    class="btn btn-primary btn-lg w-100 mb-3"
                  >
                    <i class="fas fa-calendar-check me-2"></i>
                    Book This Trip
                  </a>

                  <p class="booking-note">
                    <i class="fas fa-info-circle me-1"></i>
                    Secure your spot with easy online booking
                  </p>
                </div>
              </div>

              <!-- Trip Highlights -->
              <div class="card">
                <div class="card-body">
                  <h5 class="card-title">
                    <i class="fas fa-star me-2 text-warning"></i>
                    Why Choose This Trip?
                  </h5>
                  <ul class="highlight-list">
                    <li>
                      <i class="fas fa-check text-success me-2"></i>
                      Expert local guides
                    </li>
                    <li>
                      <i class="fas fa-check text-success me-2"></i>
                      Small group experience
                    </li>
                    <li>
                      <i class="fas fa-check text-success me-2"></i>
                      Authentic cultural immersion
                    </li>
                    <li>
                      <i class="fas fa-check text-success me-2"></i>
                      24/7 support during trip
                    </li>
                    <li>
                      <i class="fas fa-check text-success me-2"></i>
                      Flexible booking options
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Lambda API Status -->
      <div class="lambda-status text-center py-3 bg-light">
        <small class="text-muted">
          <i class="fas fa-bolt text-success me-1"></i>
          Powered by AWS Lambda API • Serverless Architecture
        </small>
      </div>
    </div>

    <!-- Image Gallery Modal -->
    <MediaViewer
      v-if="showGallery && trip.images"
      :media="trip.images"
      :initial-index="galleryIndex"
      @close="showGallery = false"
    />
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import MediaViewer from "@/components/MediaViewer.vue";

export default {
  name: "PublicTripDetail",
  components: {
    MediaViewer,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      trip: null,
      isLoading: false,
      error: null,
      currentImageIndex: 0,
      showGallery: false,
      galleryIndex: 0,
    };
  },
  computed: {
    currentImage() {
      if (this.trip?.images && this.trip.images.length > 0) {
        return this.trip.images[this.currentImageIndex];
      }
      return null;
    },
  },
  methods: {
    async loadTrip() {
      this.isLoading = true;
      this.error = null;

      try {
        const response = await axios.get(`/api/public/trips/${this.id}`);
        this.trip = response.data.trip;
      } catch (error) {
        console.error("Error loading trip:", error);
        if (error.response?.status === 404) {
          this.error = "Trip not found";
        } else {
          this.error = "Failed to load trip details. Please try again.";
        }
      } finally {
        this.isLoading = false;
      }
    },

    formatDate(dateString) {
      return moment(dateString).format("MMMM DD, YYYY");
    },

    openGallery(index) {
      this.galleryIndex = index;
      this.showGallery = true;
    },
  },

  async created() {
    await this.loadTrip();

    // Auto-rotate images if multiple images exist
    if (this.trip?.images && this.trip.images.length > 1) {
      setInterval(() => {
        this.currentImageIndex =
          (this.currentImageIndex + 1) % this.trip.images.length;
      }, 5000);
    }
  },

  beforeRouteUpdate(to, from, next) {
    this.loadTrip();
    next();
  },
};
</script>

<style scoped>
.trip-detail {
  min-height: 100vh;
}

/* Hero Section */
.trip-hero {
  position: relative;
  height: 60vh;
  min-height: 500px;
  overflow: hidden;
}

.hero-gallery {
  position: relative;
  width: 100%;
  height: 100%;
}

.main-image-container {
  width: 100%;
  height: 100%;
  position: relative;
}

.main-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: pointer;
  transition: transform 0.3s ease;
}

.main-image:hover {
  transform: scale(1.05);
}

.main-image-placeholder {
  width: 100%;
  height: 100%;
  background: var(--light-color);
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-nav {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 0.5rem;
}

.nav-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid white;
  background: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: all 0.3s ease;
}

.nav-dot.active {
  background: white;
  transform: scale(1.2);
}

.image-counter {
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
}

.hero-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
  color: white;
  padding: 3rem 0 2rem;
}

.hero-content {
  text-align: center;
}

.trip-title {
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
}

.trip-badges {
  display: flex;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.badge {
  font-size: 1rem;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
}

/* Trip Details Section */
.trip-details {
  padding: 3rem 0;
  background: #f8f9fa;
}

.card {
  border: none;
  border-radius: 15px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.card-title {
  color: var(--primary-color);
  font-weight: 600;
  border-bottom: 2px solid var(--primary-color);
  padding-bottom: 0.5rem;
  margin-bottom: 1.5rem;
}

.trip-description {
  font-size: 1.1rem;
  line-height: 1.8;
  color: #444;
  margin-bottom: 2rem;
}

.trip-meta-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: var(--light-color);
  border-radius: 10px;
}

.meta-icon {
  width: 50px;
  height: 50px;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.meta-content {
  display: flex;
  flex-direction: column;
}

.meta-content strong {
  color: var(--primary-color);
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.video-container {
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.trip-video {
  width: 100%;
  max-height: 400px;
}

/* Booking Card */
.booking-card {
  background: linear-gradient(
    135deg,
    var(--primary-color),
    var(--secondary-color)
  );
  color: white;
  border: none;
  position: sticky;
  top: 2rem;
}

.booking-title {
  font-weight: 700;
  margin-bottom: 1rem;
}

.price-display {
  margin-bottom: 1.5rem;
}

.price {
  font-size: 2.5rem;
  font-weight: 700;
  display: block;
  line-height: 1;
}

.price-unit {
  font-size: 0.9rem;
  opacity: 0.9;
}

.booking-info {
  background: rgba(255, 255, 255, 0.1);
  padding: 1rem;
  border-radius: 10px;
  margin-bottom: 1.5rem;
}

.info-item {
  margin-bottom: 0.5rem;
}

.info-item:last-child {
  margin-bottom: 0;
}

.booking-note {
  font-size: 0.9rem;
  opacity: 0.9;
  margin: 0;
}

.btn-primary {
  background: white;
  color: var(--primary-color);
  border: none;
  font-weight: 600;
  border-radius: 25px;
  padding: 0.75rem 1.5rem;
}

.btn-primary:hover {
  background: rgba(255, 255, 255, 0.9);
  color: var(--primary-color);
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.highlight-list {
  list-style: none;
  padding: 0;
}

.highlight-list li {
  padding: 0.5rem 0;
  border-bottom: 1px solid #eee;
}

.highlight-list li:last-child {
  border-bottom: none;
}

/* Responsive Design */
@media (max-width: 768px) {
  .trip-hero {
    height: 50vh;
    min-height: 300px;
  }

  .trip-title {
    font-size: 2rem;
  }

  .trip-badges {
    flex-direction: column;
    align-items: center;
  }

  .badge {
    font-size: 0.9rem;
    padding: 0.5rem 1rem;
  }

  .trip-meta-grid {
    grid-template-columns: 1fr;
  }

  .price {
    font-size: 2rem;
  }

  .booking-card {
    position: static;
  }
}

@media (max-width: 576px) {
  .trip-details {
    padding: 2rem 0;
  }

  .hero-overlay {
    padding: 2rem 0 1rem;
  }

  .meta-item {
    padding: 0.75rem;
  }

  .meta-icon {
    width: 40px;
    height: 40px;
    font-size: 1rem;
  }
}
</style>
